import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Plus,
  Check,
  X,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  Trash2,
  Settings,
  Shield,
  Clock,
  Link2,
  Unlink
} from 'lucide-react';
import Button from '../../components/ui/Button';
import {
  SocialPlatform,
  SocialAccount,
  PLATFORM_CONFIGS
} from '../../types/contentHub';
import { contentHubService } from '../../services/contentHubService';
import socialService from '../../services/socialService';
import { useProject } from '../../contexts/ProjectContext';
import toast from 'react-hot-toast';

// Platform Icon Component
const PlatformIcon: React.FC<{ platform: SocialPlatform; size?: number; light?: boolean }> = ({ platform, size = 24, light = false }) => {
  const config = PLATFORM_CONFIGS[platform];
  const icons: Record<SocialPlatform, React.ReactNode> = {
    instagram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    facebook: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    twitter: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    youtube: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    youtube_shorts: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 14.65v-5.3L15 12l-5 2.65zm7.77-4.33c-.77-.32-1.2-.5-1.2-.5L18 9.06c1.84-.96 2.53-3.23 1.56-5.06s-3.24-2.53-5.07-1.56L6 6.94c-1.29.68-2.07 2.04-2 3.49.07 1.42.93 2.67 2.22 3.25.03.01 1.2.5 1.2.5L6 14.93c-1.83.97-2.53 3.24-1.56 5.07.97 1.83 3.24 2.53 5.07 1.56l8.5-4.5c1.29-.68 2.06-2.04 1.99-3.49-.07-1.42-.94-2.68-2.23-3.25z"/>
      </svg>
    ),
    tiktok: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    ),
    threads: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.023.88-.73 2.106-1.146 3.451-1.175 1.016-.022 1.96.139 2.818.468-.073-.938-.306-1.683-.7-2.216-.453-.614-1.143-.936-2.053-.958l-.023-.003c-.689 0-1.58.142-2.433.907l-1.416-1.486c1.249-1.12 2.664-1.538 4.001-1.538h.036c1.478.034 2.689.548 3.6 1.528.857.922 1.366 2.2 1.514 3.8.475.19.917.422 1.32.695 1.198.813 2.074 1.922 2.533 3.205.752 2.106.593 4.932-1.635 7.11C18.581 23.087 15.994 23.973 12.186 24z"/>
      </svg>
    )
  };

  return <span style={{ color: light ? '#ffffff' : config.color }}>{icons[platform]}</span>;
};

// Platform Card Component
const PlatformCard: React.FC<{
  platform: SocialPlatform;
  accounts: SocialAccount[];
  onConnect: () => void;
  onDisconnect: (accountId: string) => void;
  onRefresh: (accountId: string) => void;
  isConnecting?: boolean;
}> = ({ platform, accounts, onConnect, onDisconnect, onRefresh, isConnecting }) => {
  const { t } = useTranslation();
  const config = PLATFORM_CONFIGS[platform];
  const connectedAccounts = accounts.filter(a => a.platform === platform);
  const hasConnected = connectedAccounts.length > 0;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border-2 overflow-hidden transition-all ${
      hasConnected ? 'border-green-200 dark:border-green-800' : 'border-gray-200 dark:border-gray-700'
    }`}>
      {/* Header */}
      <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-3 rounded-xl ${config.bgColor}`}>
              <PlatformIcon platform={platform} size={24} light />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 dark:text-white">{config.name}</h3>
              <p className="text-sm text-gray-500">
                {hasConnected
                  ? t('integrations.connectedAccounts', '{{count}} account(s) connected', { count: connectedAccounts.length })
                  : t('integrations.notConnected', 'Not connected')
                }
              </p>
            </div>
          </div>

          {hasConnected ? (
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                <Check className="w-3.5 h-3.5" />
                {t('integrations.connected', 'Connected')}
              </span>
            </div>
          ) : (
            <Button
              onClick={onConnect}
              disabled={isConnecting}
              className="flex-shrink-0 h-10"
            >
              {isConnecting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              {t('integrations.connect', 'Connect')}
            </Button>
          )}
        </div>
      </div>

      {/* Connected Accounts List */}
      {hasConnected && (
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {connectedAccounts.map(account => (
            <div key={account.id} className="p-4 flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                {account.profileImage ? (
                  <img
                    src={account.profileImage}
                    alt={account.accountName}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <PlatformIcon platform={platform} size={20} />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{account.accountName}</p>
                  <p className="text-sm text-gray-500 truncate">@{account.accountHandle}</p>
                </div>
              </div>

              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                {/* Connection Status */}
                {account.connectionStatus === 'expired' && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                    <AlertCircle className="w-3 h-3" />
                    {t('integrations.tokenExpired', 'Token expired')}
                  </span>
                )}
                {account.connectionStatus === 'error' && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                    <AlertCircle className="w-3 h-3" />
                    {t('integrations.connectionError', 'Error')}
                  </span>
                )}

                {/* Actions */}
                <button
                  onClick={() => onRefresh(account.id)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title={t('integrations.refreshToken', 'Refresh connection')}
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDisconnect(account.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('integrations.disconnect', 'Disconnect')}
                >
                  <Unlink className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Add Another Account */}
          <div className="p-4">
            <button
              onClick={onConnect}
              disabled={isConnecting}
              className="w-full py-2 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-lg text-gray-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              {t('integrations.addAnotherAccount', 'Add another account')}
            </button>
          </div>
        </div>
      )}

      {/* Platform Features */}
      {!hasConnected && (
        <div className="px-4 py-3 sm:px-5 bg-gray-50 dark:bg-gray-900/40">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('integrations.features', 'Features:')}</p>
          <div className="flex flex-wrap gap-2">
            {config.supportedFormats.map(format => (
              <span key={format} className="px-2 py-1 bg-white dark:bg-gray-800 rounded text-xs text-gray-600 dark:text-gray-300 capitalize">
                {format}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const IntegrationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentProject } = useProject();

  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectingPlatform, setConnectingPlatform] = useState<SocialPlatform | null>(null);

  // Fetch connected accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      setIsLoading(true);
      try {
        const response = await socialService.getAccounts();
        if (response.success && response.data) {
          // Map backend response to SocialAccount format
          const mappedAccounts: SocialAccount[] = response.data.map((acc: any) => ({
            id: acc.id,
            platform: acc.platform as SocialPlatform,
            accountName: acc.username || acc.account_name,
            accountHandle: acc.username,
            profileImage: acc.profile_image,
            isConnected: acc.connected,
            connectionStatus: acc.connected ? 'connected' : 'error',
            lastSynced: acc.last_sync,
            permissions: []
          }));
          setAccounts(mappedAccounts);
        } else {
          setAccounts([]);
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
        setAccounts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  const handleConnect = async (platform: SocialPlatform) => {
    setConnectingPlatform(platform);
    try {
      const response = await socialService.getOAuthUrl(platform, currentProject?.id);
      if (response.success && response.authUrl) {
        window.location.href = response.authUrl;
      } else {
        toast.error(response.message || t('errors.oauthNotConfigured', 'OAuth not configured for this platform'));
      }
    } catch (error: any) {
      console.error('Error connecting account:', error);
      toast.error(error.response?.data?.message || t('integrations.connectionError', 'Error connecting account. Please try again.'));
    } finally {
      setConnectingPlatform(null);
    }
  };

  const handleDisconnect = async (accountId: string) => {
    if (!window.confirm(t('integrations.confirmDisconnect', 'Are you sure you want to disconnect this account?'))) {
      return;
    }

    try {
      await socialService.disconnectAccount(accountId);
      setAccounts(prev => prev.filter(a => a.id !== accountId));
      toast.success(t('integrations.disconnected', 'Account disconnected'));
    } catch (error) {
      console.error('Error disconnecting account:', error);
      toast.error(t('errors.generic', 'Error disconnecting'));
    }
  };

  const handleRefresh = async (accountId: string) => {
    try {
      await socialService.syncAccount(accountId);
      toast.success(t('integrations.synced', 'Account synced'));
      // Reload accounts
      const response = await socialService.getAccounts();
      if (response.success && response.data) {
        const mappedAccounts: SocialAccount[] = response.data.map((acc: any) => ({
          id: acc.id,
          platform: acc.platform as SocialPlatform,
          accountName: acc.username || acc.account_name,
          accountHandle: acc.username,
          profileImage: acc.profile_image,
          isConnected: acc.connected,
          connectionStatus: acc.connected ? 'connected' : 'error',
          lastSynced: acc.last_sync,
          permissions: []
        }));
        setAccounts(mappedAccounts);
      }
    } catch (error) {
      console.error('Error syncing account:', error);
      toast.error(t('errors.generic', 'Error syncing'));
    }
  };

  const connectedCount = accounts.filter(a => a.isConnected).length;
  const platforms = Object.values(PLATFORM_CONFIGS);

  return (
    <div className="max-w-5xl space-y-4 sm:space-y-6">
      {/* Cabeçalho: voltar para Redes sociais + título + explicação */}
      <div className="flex items-start gap-2 sm:gap-3">
        <button
          onClick={() => navigate('/content')}
          aria-label={t('common.back', 'Voltar')}
          className="mt-0.5 p-2 -ml-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('integrations.title', 'Social Media Integrations')}
          </h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {t('integrations.subtitle', 'Connect your social accounts to publish content directly')}
          </p>
        </div>
      </div>

      {/* Quantas redes estão ligadas */}
      <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-indigo-100 text-sm">{t('integrations.connectedPlatforms', 'Connected Platforms')}</p>
            <p className="text-3xl sm:text-4xl font-bold mt-1">{connectedCount} / {platforms.length}</p>
          </div>
          <div className="p-3 sm:p-4 bg-white/20 rounded-xl">
            <Shield className="w-7 h-7 sm:w-8 sm:h-8" />
          </div>
        </div>
        <div className="mt-3 sm:mt-4 flex items-center gap-2 text-indigo-100 text-sm">
          <Clock className="w-4 h-4 flex-shrink-0" />
          {t('integrations.secureConnection', 'All connections are secured with OAuth 2.0')}
        </div>
      </div>

      {/* Platforms Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {platforms.map(config => (
            <PlatformCard
              key={config.id}
              platform={config.id}
              accounts={accounts}
              onConnect={() => handleConnect(config.id)}
              onDisconnect={handleDisconnect}
              onRefresh={handleRefresh}
              isConnecting={connectingPlatform === config.id}
            />
          ))}
        </div>
      )}
      {/* (A caixa "Precisa de ajuda?" saiu: os três links não levavam a lugar nenhum.) */}
    </div>
  );
};

export default IntegrationsPage;
